import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Tag, Flame, Minus, Plus, Trash2, Home } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [isCartMode, setIsCartMode] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [shippingInfo, setShippingInfo] = useState({});
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [placing, setPlacing] = useState(false);
  const [placeError, setPlaceError] = useState("");
  const [stripeUrl, setStripeUrl] = useState("");
  const [createdOrderIds, setCreatedOrderIds] = useState([]);
  const [createdCartGroupId, setCreatedCartGroupId] = useState("");
  const placingRef = useRef(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  useEffect(() => {
    const mode = sessionStorage.getItem("checkout_mode");
    if (mode === "cart") {
      const items = JSON.parse(sessionStorage.getItem("cart_items") || "[]");
      const shipping = JSON.parse(sessionStorage.getItem("cart_shipping") || "{}");
      if (items.length === 0) { navigate("/"); return; }
      setCartItems(items);
      setShippingInfo(shipping);
      setIsCartMode(true);
    } else {
      const data = sessionStorage.getItem("order_data");
      if (!data) { navigate("/"); return; }
      const parsed = JSON.parse(data);
      setOrderData(parsed);
      setShippingInfo(parsed);
    }

    const sessionPromo = sessionStorage.getItem("promo_code");
    const sessionDiscount = sessionStorage.getItem("promo_discount");
    const sessionPromoType = sessionStorage.getItem("promo_type");
    if (sessionPromo && sessionDiscount) {
      setPromoCode(sessionPromo);
      setAppliedPromo({ code: sessionPromo, discount_percent: parseInt(sessionDiscount), promo_type: sessionPromoType || "percentage" });
    }
  }, [navigate]);

  const applyPromo = async () => {
    setPromoError("");
    try {
      const res = await axios.post(`${API}/promo-codes/apply`, { code: promoCode.toUpperCase() });
      setAppliedPromo(res.data);
    } catch (e) {
      setPromoError(e.response?.data?.detail || "Invalid promo code");
      setAppliedPromo(null);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    setPromoError("");
    sessionStorage.removeItem("promo_code");
    sessionStorage.removeItem("promo_discount");
    sessionStorage.removeItem("promo_type");
  };

  // Cart modification in checkout
  const updateCartQty = (idx, delta) => {
    const next = [...cartItems];
    next[idx] = { ...next[idx], quantity: Math.max(1, (next[idx].quantity || 1) + delta) };
    setCartItems(next);
    sessionStorage.setItem("cart_items", JSON.stringify(next));
  };

  const removeCartItem = (idx) => {
    const next = cartItems.filter((_, i) => i !== idx);
    setCartItems(next);
    sessionStorage.setItem("cart_items", JSON.stringify(next));
    if (next.length === 0) navigate("/");
  };

  const updateSingleQty = (delta) => {
    const next = { ...orderData, quantity: Math.max(1, (orderData.quantity || 1) + delta) };
    setOrderData(next);
    sessionStorage.setItem("order_data", JSON.stringify(next));
  };

  // Calculate totals for single item mode
  const getSingleSubtotal = () => {
    if (!orderData) return 0;
    const qty = orderData.quantity || 1;
    return (orderData.product_price + (orderData.front_engraving_total || 0) + (orderData.back_engraving_total || 0)) * qty;
  };

  const getSingleDiscount = () => {
    if (!appliedPromo) return 0;
    const subtotal = getSingleSubtotal();
    const promoType = appliedPromo.promo_type || "percentage";

    if (promoType === "material_only") {
      const qty = orderData?.quantity || 1;
      return ((orderData?.front_engraving_total || 0) + (orderData?.back_engraving_total || 0)) * qty;
    }

    const pct = appliedPromo.discount_percent || 0;
    if (pct >= 100) {
      // 100% off applies to 1 unit only
      const perUnit = (orderData?.product_price || 0) + (orderData?.front_engraving_total || 0) + (orderData?.back_engraving_total || 0);
      return perUnit;
    }
    return subtotal * (pct / 100);
  };

  // Calculate totals for cart mode
  const getCartSubtotal = () => {
    return cartItems.reduce((sum, item) => {
      const qty = item.quantity || 1;
      return sum + (item.product_price + (item.front_engraving_total || 0) + (item.back_engraving_total || 0)) * qty;
    }, 0);
  };

  const getCartDiscount = () => {
    if (!appliedPromo) return 0;
    // Promo applies to the most expensive item only
    const promoType = appliedPromo.promo_type || "percentage";
    let maxPerUnit = 0;
    let maxItem = null;

    for (const item of cartItems) {
      const perUnit = item.product_price + (item.front_engraving_total || 0) + (item.back_engraving_total || 0);
      if (perUnit > maxPerUnit) {
        maxPerUnit = perUnit;
        maxItem = item;
      }
    }

    if (!maxItem) return 0;

    if (promoType === "material_only") {
      const qty = maxItem.quantity || 1;
      return ((maxItem.front_engraving_total || 0) + (maxItem.back_engraving_total || 0)) * qty;
    }

    const pct = appliedPromo.discount_percent || 0;
    if (pct >= 100) {
      // 100% off applies to 1 unit of the most expensive item
      return maxPerUnit;
    }

    const itemSubtotal = maxPerUnit * (maxItem.quantity || 1);
    return itemSubtotal * (pct / 100);
  };

  const getSubtotal = () => isCartMode ? getCartSubtotal() : getSingleSubtotal();
  const getDiscount = () => isCartMode ? getCartDiscount() : getSingleDiscount();
  const getTotal = () => Math.max(0, getSubtotal() - getDiscount());

  const getPromoLabel = () => {
    if (!appliedPromo) return "";
    if (appliedPromo.promo_type === "material_only") return "Material Only (Engraving Free)";
    return `${appliedPromo.discount_percent}% OFF`;
  };

  const handlePlaceOrder = async () => {
    if (placingRef.current) return;
    placingRef.current = true;
    setPlacing(true);
    setPlaceError("");

    try {
      let orderIds = createdOrderIds;
      let cartGroupId = createdCartGroupId;

      // Only create orders if we haven't already (prevents duplicates on retry)
      if (orderIds.length === 0) {
        if (isCartMode) {
          const cartPayload = {
            items: cartItems.map(item => ({
              product_id: item.product_id,
              quantity: item.quantity || 1,
              engraving_category_ids: item.engraving_category_ids || [],
              customization_type: item.customization_type || "",
              customization_text: item.customization_text || "",
              customization_font: item.customization_font || "",
              customization_font_size: item.customization_font_size || 0,
              customization_image_path: item.customization_image_path || "",
              catalog_image_id: item.catalog_image_id || "",
              overlay_position: item.overlay_position || null,
              text_overlay_position: item.text_overlay_position || null,
              image_overlay_position: item.image_overlay_position || null,
              product_image_url: item.product_image || "",
              design_description: item.design_description || "",
              back_engraving_category_ids: item.back_engraving_category_ids || [],
              back_customization_type: item.back_customization_type || "",
              back_customization_text: item.back_customization_text || "",
              back_customization_font: item.back_customization_font || "",
              back_customization_font_size: item.back_customization_font_size || 0,
              back_customization_image_path: item.back_customization_image_path || "",
              back_catalog_image_id: item.back_catalog_image_id || "",
              back_overlay_position: item.back_overlay_position || null,
              back_text_overlay_position: item.back_text_overlay_position || null,
              back_image_overlay_position: item.back_image_overlay_position || null,
              cutout_shape_id: item.cutout_shape_id || "",
              cutout_shape_name: item.cutout_shape_name || "",
            })),
            ...shippingInfo,
            promo_code: appliedPromo?.code || ""
          };
          const cartRes = await axios.post(`${API}/orders/cart`, cartPayload);
          cartGroupId = cartRes.data.cart_group_id;
          orderIds = cartRes.data.order_ids;
        } else {
          const payload = {
            product_id: orderData.product_id,
            quantity: orderData.quantity || 1,
            engraving_category_ids: orderData.engraving_category_ids || [],
            customization_type: orderData.customization_type,
            customization_text: orderData.customization_text || "",
            customization_font: orderData.customization_font || "",
            customization_font_size: orderData.customization_font_size || 0,
            customization_image_path: orderData.customization_image_path || "",
            catalog_image_id: orderData.catalog_image_id || "",
            overlay_position: orderData.overlay_position || null,
            text_overlay_position: orderData.text_overlay_position || null,
            image_overlay_position: orderData.image_overlay_position || null,
            product_image_url: orderData.product_image || "",
            design_description: orderData.design_description || "",
            back_engraving_category_ids: orderData.back_engraving_category_ids || [],
            back_customization_type: orderData.back_customization_type || "",
            back_customization_text: orderData.back_customization_text || "",
            back_customization_font: orderData.back_customization_font || "",
            back_customization_font_size: orderData.back_customization_font_size || 0,
            back_customization_image_path: orderData.back_customization_image_path || "",
            back_catalog_image_id: orderData.back_catalog_image_id || "",
            back_overlay_position: orderData.back_overlay_position || null,
            back_text_overlay_position: orderData.back_text_overlay_position || null,
            back_image_overlay_position: orderData.back_image_overlay_position || null,
            shipping_name: orderData.shipping_name || "",
            shipping_address: orderData.shipping_address || "",
            shipping_city: orderData.shipping_city || "",
            shipping_state: orderData.shipping_state || "",
            shipping_zip: orderData.shipping_zip || "",
            shipping_email: orderData.shipping_email || "",
            pickup_on_site: orderData.pickup_on_site || false,
            promo_code: appliedPromo?.code || ""
          };
          const orderRes = await axios.post(`${API}/orders`, payload);
          orderIds = [orderRes.data.id];
        }
        // Save order IDs so retries don't create duplicates
        setCreatedOrderIds(orderIds);
        setCreatedCartGroupId(cartGroupId);
      }

      // Create Stripe checkout session
      const origin = window.location.origin;
      const stripePayload = {
        origin_url: origin,
        ...(cartGroupId ? { cart_group_id: cartGroupId } : { order_id: orderIds[0] })
      };

      let stripeRes;
      try {
        stripeRes = await axios.post(`${API}/payments/create-session`, stripePayload);
      } catch (stripeErr) {
        
        const detail = stripeErr.response?.data?.detail || stripeErr.message || "Unknown error";
        setPlaceError(`Payment setup failed: ${detail}`);
        setPlacing(false);
        placingRef.current = false;
        return;
      }

      if (stripeRes.data.free_order) {
        setOrderPlaced(true);
        sessionStorage.removeItem("order_data");
        sessionStorage.removeItem("cart_items");
        sessionStorage.removeItem("cart_shipping");
        sessionStorage.removeItem("checkout_mode");
        sessionStorage.removeItem("promo_code");
        sessionStorage.removeItem("promo_discount");
        sessionStorage.removeItem("promo_type");
        setPlacing(false);
        placingRef.current = false;
        return;
      }

      if (stripeRes.data.url) {
        // Store URL as fallback before attempting redirect
        setStripeUrl(stripeRes.data.url);
        // Attempt redirect
        window.location.href = stripeRes.data.url;
        // Keep button disabled — if redirect fails, the fallback link will show
        return;
      } else {
        setPlaceError("Could not connect to payment provider. Please try again.");
        setPlacing(false);
        placingRef.current = false;
      }
    } catch (e) {
      
      const msg = e.response?.data?.detail || "Something went wrong. Please try again.";
      setPlaceError(msg);
      setPlacing(false);
      placingRef.current = false;
    }
  };

  if (!orderData && cartItems.length === 0 && !orderPlaced) return null;

  if (orderPlaced) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" data-testid="order-success">
        <div className="brutal-card p-12 text-center max-w-md">
          <div className="w-16 h-16 bg-[#00E5FF] border-2 border-black flex items-center justify-center mx-auto mb-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <Check size={32} strokeWidth={2.5} />
          </div>
          <h2 className="font-heading text-2xl font-black uppercase mb-4">Order Placed!</h2>
          <p className="text-zinc-500 mb-8">Your custom laser engraving order has been received. We will get started right away!</p>
          <button onClick={() => navigate("/")} className="brutal-btn text-sm" data-testid="back-to-shop-btn">
            <Flame size={14} className="inline mr-2" strokeWidth={2.5} />
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  // Items to display
  const displayItems = isCartMode ? cartItems : (orderData ? [orderData] : []);

  return (
    <div className="min-h-screen" data-testid="checkout-page">
      <div className="border-b-2 border-black bg-white px-6 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/shipping")} className="flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wider hover:text-[#FF3366] transition-colors" data-testid="back-to-shipping-btn">
          <ArrowLeft size={18} strokeWidth={2.5} /> Back
        </button>
        <span className="font-heading font-bold text-sm uppercase tracking-wider">Checkout</span>
        <button onClick={() => navigate("/")} className="flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wider hover:text-[#FF3366] transition-colors" data-testid="continue-shopping-checkout">
          <Home size={18} strokeWidth={2.5} /> Shop
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-8 py-12">
        {/* Order Breakdown */}
        <div className="brutal-card p-8 mb-8" data-testid="order-breakdown">
          <h3 className="font-heading text-lg font-bold uppercase mb-6">Order Summary</h3>

          <div className="space-y-4 border-b-2 border-black pb-6 mb-6">
            {displayItems.map((item, idx) => (
              <div key={item.id || item.product_id || idx} className="space-y-2">
                {idx > 0 && <div className="border-t border-zinc-200 my-3" />}
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <span className="font-heading font-bold text-sm uppercase">{item.product_name}</span>
                    <span className="block text-xs text-zinc-500">Product — ${(item.product_price || 0).toFixed(2)} each</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Quantity controls */}
                    <div className="flex items-center gap-1">
                      <button onClick={() => isCartMode ? updateCartQty(idx, -1) : updateSingleQty(-1)}
                        className="w-6 h-6 border-2 border-black flex items-center justify-center bg-white hover:bg-zinc-100 text-xs"
                        data-testid={`checkout-qty-dec-${idx}`}>
                        <Minus size={10} strokeWidth={3} />
                      </button>
                      <span className="font-heading font-black text-sm w-6 text-center" data-testid={`checkout-qty-${idx}`}>{item.quantity || 1}</span>
                      <button onClick={() => isCartMode ? updateCartQty(idx, 1) : updateSingleQty(1)}
                        className="w-6 h-6 border-2 border-black flex items-center justify-center bg-white hover:bg-zinc-100 text-xs"
                        data-testid={`checkout-qty-inc-${idx}`}>
                        <Plus size={10} strokeWidth={3} />
                      </button>
                    </div>
                    {isCartMode && displayItems.length > 1 && (
                      <button onClick={() => removeCartItem(idx)} className="text-red-500 hover:text-red-700 p-0.5" data-testid={`checkout-remove-${idx}`}>
                        <Trash2 size={14} strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                </div>
                {(item.front_engravings || []).map(eng => (
                  <div key={eng.id} className="flex justify-between items-center">
                    <div>
                      <span className="font-heading font-bold text-sm uppercase">{eng.name}</span>
                      <span className="block text-xs text-zinc-500">Front Engraving</span>
                    </div>
                    <span className="font-heading font-bold">${eng.price.toFixed(2)}</span>
                  </div>
                ))}
                {(item.back_engravings || []).map(eng => (
                  <div key={eng.id} className="flex justify-between items-center">
                    <div>
                      <span className="font-heading font-bold text-sm uppercase">{eng.name}</span>
                      <span className="block text-xs text-zinc-500">Back Engraving (50%)</span>
                    </div>
                    <span className="font-heading font-bold">${eng.price.toFixed(2)}</span>
                  </div>
                ))}
                {item.cutout_shape_name && (
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-heading font-bold text-sm uppercase">Cutout: {item.cutout_shape_name}</span>
                      <span className="block text-xs text-zinc-500">Shape Cut</span>
                    </div>
                  </div>
                )}
                {/* Customization details */}
                {(item.customization_text || item.customization_font || item.design_description) && (
                  <div className="bg-zinc-50 border-2 border-dashed border-zinc-300 p-3 mt-1 space-y-1">
                    {item.customization_text && (
                      <div className="flex items-start gap-2">
                        <span className="text-[10px] text-zinc-400 font-heading uppercase shrink-0 w-12">Text:</span>
                        <span className="text-xs text-zinc-700 font-mono">&ldquo;{item.customization_text}&rdquo;</span>
                      </div>
                    )}
                    {item.customization_font && (
                      <div className="flex items-start gap-2">
                        <span className="text-[10px] text-zinc-400 font-heading uppercase shrink-0 w-12">Font:</span>
                        <span className="text-xs text-zinc-700">{item.customization_font}{item.customization_font_size ? ` (${item.customization_font_size}px)` : ""}</span>
                      </div>
                    )}
                    {item.design_description && (
                      <div className="flex items-start gap-2">
                        <span className="text-[10px] text-zinc-400 font-heading uppercase shrink-0 w-12">Notes:</span>
                        <span className="text-xs text-zinc-700">{item.design_description}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Subtotal */}
          <div className="flex justify-between items-center mb-3">
            <span className="font-heading font-bold text-sm uppercase text-zinc-500">Subtotal</span>
            <span className="font-heading font-bold">${getSubtotal().toFixed(2)}</span>
          </div>

          {/* Discount */}
          {appliedPromo && getDiscount() > 0 && (
            <div className="space-y-1 mb-3">
              <div className="flex justify-between items-center text-[#FF3366]" data-testid="discount-line">
                <span className="font-heading font-bold text-sm uppercase">
                  Promo: {getPromoLabel()}
                </span>
                <span className="font-heading font-bold">-${getDiscount().toFixed(2)}</span>
              </div>
              {appliedPromo.discount_percent >= 100 && (
                <p className="text-[10px] text-zinc-400">100% discount applies to 1 item only.</p>
              )}
            </div>
          )}

          {/* Total */}
          <div className="flex justify-between items-center border-t-2 border-black pt-4 mt-4">
            <span className="font-heading font-black text-xl uppercase">Total</span>
            <span className="font-heading font-black text-3xl text-[#FF3366]" data-testid="order-total">
              ${getTotal().toFixed(2)}
            </span>
          </div>
        </div>

        {/* Promo Code */}
        <div className="brutal-card p-6 mb-8" data-testid="promo-section">
          <div className="flex items-center gap-2 mb-4">
            <Tag size={16} strokeWidth={2.5} />
            <span className="font-heading font-bold text-sm uppercase">Promo Code</span>
            {isCartMode && <span className="text-[10px] text-zinc-400">(applies to 1 item only)</span>}
          </div>
          {appliedPromo ? (
            <div className="flex items-center justify-between bg-[#00E5FF] border-2 border-black p-3">
              <span className="font-heading font-bold text-sm">
                {appliedPromo.code} ({getPromoLabel()})
              </span>
              <button onClick={removePromo} className="font-heading font-bold text-xs uppercase text-[#FF3366] hover:underline" data-testid="remove-promo-btn">
                Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <input className="brutal-input flex-1" placeholder="Enter promo code" value={promoCode}
                onChange={e => setPromoCode(e.target.value)} data-testid="promo-code-input" />
              <button onClick={applyPromo} disabled={!promoCode}
                className={`brutal-btn brutal-btn-secondary text-sm ${!promoCode ? "opacity-50" : ""}`}
                data-testid="apply-promo-btn">Apply</button>
            </div>
          )}
          {promoError && <p className="text-[#FF3366] text-xs font-bold mt-2" data-testid="promo-error">{promoError}</p>}
        </div>

        {/* Shipping Info */}
        <div className="brutal-card p-6 mb-8" data-testid="shipping-summary">
          <span className="font-heading font-bold text-sm uppercase block mb-3">
            {shippingInfo.pickup_on_site ? "Pick Up On Site" : "Shipping To"}
          </span>
          {shippingInfo.pickup_on_site ? (
            <div className="flex items-center gap-2">
              <Badge className="bg-[#00E5FF] text-black border-2 border-black rounded-none font-mono text-[10px]">EVENT PICKUP</Badge>
              <span className="text-sm">{shippingInfo.shipping_email}</span>
            </div>
          ) : (
            <div className="text-sm space-y-1">
              <p className="font-bold">{shippingInfo.shipping_name}</p>
              <p>{shippingInfo.shipping_address}</p>
              <p>{shippingInfo.shipping_city}, {shippingInfo.shipping_state} {shippingInfo.shipping_zip}</p>
              <p className="text-zinc-500">{shippingInfo.shipping_email}</p>
            </div>
          )}
        </div>

        {/* Place Order */}
        {placeError && (
          <div className="bg-red-50 border-2 border-red-500 p-3 text-sm text-red-700 font-heading" data-testid="place-order-error">
            {placeError}
          </div>
        )}
        {stripeUrl && (
          <div className="bg-green-50 border-2 border-green-500 p-4 text-center" data-testid="stripe-fallback">
            <p className="text-sm text-zinc-700 mb-3 font-heading">Your order is ready! Tap below to complete payment:</p>
            <a href={stripeUrl} className="brutal-btn inline-block text-center text-sm w-full" data-testid="stripe-fallback-link">
              Proceed to Secure Payment →
            </a>
          </div>
        )}
        {!stripeUrl && (
          <button onClick={handlePlaceOrder} disabled={placing}
            className={`brutal-btn w-full text-center text-sm ${placing ? "opacity-50 pointer-events-none" : ""}`}
            data-testid="place-order-btn">
            {placing ? "Redirecting to Payment..." : createdOrderIds.length > 0 ? "Retry Payment" : `Place Order - $${getTotal().toFixed(2)}`}
          </button>
        )}
      </div>
    </div>
  );
}
